import { Icon } from '../atoms/Icon';

export type StepStatus = 'active' | 'upcoming' | 'completed';

export type StepConfig = {
  id: string | number;
  label: string;
  description?: string;
  status: StepStatus;
};

type VerticalStepperProps = {
  steps: StepConfig[];
  onStepClick?: (id: string | number) => void;
};

const StepIndicator = ({ step, index }: { step: StepConfig; index: number }) => {
  if (step.status === 'completed') {
    return (
      <div className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-success-500'>
        <Icon type='check-circle' size='20' stroke='white' />
      </div>
    );
  }
  if (step.status === 'active') {
    return (
      <div className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-500'>
        <span className="text-sm font-medium leading-5 text-white">{index + 1}</span>
      </div>
    );
  }
  // upcoming
  return (
    <div className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100'>
      <Icon type='check-circle' size='20' stroke='gray-400' />
    </div>
  );
};

export const VerticalStepper = ({ steps, onStepClick }: VerticalStepperProps) => {
  return (
    <div className='relative flex flex-col gap-6'>
      {/* Vertical connecting line between step 1 and step 2 */}
      {steps.length > 1 && (
        <div
          className='absolute left-4 top-8 w-px bg-gray-100'
          style={{ height: 'calc(100% - 4rem)' }}
        />
      )}
      {steps.map((step, index) => (
        <div
          key={step.id}
          className={`relative flex items-start gap-3 ${onStepClick ? 'cursor-pointer' : ''}`}
          onClick={() => onStepClick?.(step.id)}
          role={onStepClick ? 'button' : undefined}
          tabIndex={onStepClick ? 0 : undefined}
          onKeyDown={onStepClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') onStepClick(step.id); } : undefined}
        >
          <StepIndicator step={step} index={index} />
          <div className='flex flex-col'>
            <span
              className="text-sm font-medium leading-5"
              style={{ color: step.status === 'active' ? '#2f37ed' : '#0f1728' }}
            >
              {step.label}
            </span>
            {step.description && (
              <span className="text-xs font-normal leading-[18px] text-gray-400">
                {step.description}
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};
